import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { ALLOWED_ROLES, getDivsWithAccess, isPackedClaims, resolveOrgAccess } from '../access';

/**
 * Permitted divisions by organization.
 * @param {readonly string[]} [roles]
 */
const useGetDivisionsAllowed = (roles = ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.LIST) => {
  const orgId = useSelector(({ organizations }) => organizations.organization.id);
  const selectedDivsIds = useSelector(({ divisions }) => divisions.selector.ids || []);
  const claims = useSelector(({ user }) => user?.access?.data?.claims);
  const orgUserAccess = useSelector(({ user }) => user?.orgUser?.data?.access);
  const accessLoaded = useSelector(({ user }) => user?.access?.loaded);
  const orgUserLoaded = useSelector(({ user }) => user?.orgUser?.loaded);
  const assignedLoaded = useSelector(({ divisions }) => divisions?.assigned?.loaded);
  const assignedOrgId = useSelector(({ divisions }) => divisions?.assigned?.data?.orgId);
  const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions ?? {});

  const allowedDivs = useMemo(
    () => getDivsWithAccess(roles, userOrgAccess, userOrgDivisions),
    [roles, userOrgAccess, userOrgDivisions],
  );
  const allowedDivsIds = useMemo(() => allowedDivs.map(d => d.id), [allowedDivs]);
  const allowedSelectedDivs = useMemo(
    () => selectedDivsIds.filter(divId => allowedDivsIds.includes(divId)),
    [allowedDivsIds, selectedDivsIds],
  );

  return {
    permissionsLoaded: Boolean(
      orgId && accessLoaded && assignedLoaded && assignedOrgId === orgId &&
      (!isPackedClaims(claims) || orgUserLoaded),
    ),
    allowedSelectedDivs, // Divisions allowed controlled by division selector
    allowedDivs, // All divisions allowed
    allowedDivsIds, // All divisions allowed ids
  };
};

export default useGetDivisionsAllowed;
